import * as React from 'react'

import { CalendarX2 } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { ILocationClosureInterval } from '~/client/graph'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import Closure from '../../models/Closure'
import ClosuresStore from '../../stores/domain/Closures.store'
import {
  buildEffectiveOperatingIntervalsFmt,
  resolveOperatingWindowForDateFmt,
} from '../../utils/intervalFormatters'
import { isLocationOpen } from '../../utils/locationOpenState'
import OpeningIndicatorLabel from '../OpeningIndicatorLabel/OpeningIndicatorLabel'

interface IProps {
  item: MapViewItemBase
  projectDateStore?: ProjectDateStore
  closuresStore?: ClosuresStore
  selectedStartDate?: Date
  selectedEndDate?: Date
}

/** A location's name, open state and today's hours or active closures: the location card's content, on any surface. */
@inject('projectDateStore', 'closuresStore')
@observer
export default class LocationCardBody extends React.Component<IProps> {
  public render() {
    const { item } = this.props

    if (!item?.dataObject) {
      return null
    }

    return (
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1">
        <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-2">
          <SitemapAttributeTag
            shouldShowAsTag={false}
            className="tw:-ml-1"
            contentContainerClassName="tw:min-w-0 tw:truncate"
            dataObject={item.dataObject}
          >
            <span
              title={item.dataObject.name}
              className="tw:truncate tw:text-body-md"
            >
              {item.dataObject.name}
            </span>
          </SitemapAttributeTag>
          <div className="tw:flex-none">
            <OpeningIndicatorLabel
              closeLabel={Localization.translator.closed}
              openLabel={Localization.translator.opened}
              isOpen={this.isOpen}
            />
          </div>
        </div>
        {this.renderClosureContent()}
      </div>
    )
  }

  private renderClosureContent() {
    const {
      item: { dataObject },
      projectDateStore,
    } = this.props

    const { getTimeIntervalPerDayToDisplay } = projectDateStore

    const { operatingIntervals } = dataObject
    // The real instant, not convertToProjectDate's. That one restates the
    // VIEWER's wall clock as a project-local time, which is a different instant
    // whenever the two zones differ — a Tokyo viewer on an LA project would have
    // resolved Monday's window while LA was still on Sunday. The engine reads
    // instants on the project's clock itself, so it wants the instant.
    const now = new Date()

    // The window in effect right now, by the same precedence the booking form and
    // the jobsite homepage apply: a dated window governs its own date, below that
    // the weekday's, below that an every-day one, and the project's week where the
    // resource states nothing recurring of its own.
    //
    // This used to search for an interval that left the location fully closed
    // across the next 24 hours - which selects one that does NOT apply. An
    // ordinary every-day schedule matched nothing and showed no hours at all,
    // while a dated window days away was picked and its times displayed.
    const activeOperationalInterval = resolveOperatingWindowForDateFmt(
      projectDateStore,
      buildEffectiveOperatingIntervalsFmt(projectDateStore, operatingIntervals),
      now,
    )

    switch (true) {
      case !!this.activeClosures?.length:
        return this.closuresList
      case !!activeOperationalInterval:
        return (
          <div className="tw:flex tw:flex-col tw:text-body-sm">
            {getTimeIntervalPerDayToDisplay(
              activeOperationalInterval.startDate,
              activeOperationalInterval.endDate,
            )}
          </div>
        )
      default:
        return (
          <div className="tw:text-body-sm">
            {Localization.translator.noActiveClosures}
          </div>
        )
    }
  }

  private get activeClosures(): Closure[] {
    const {
      item: { dataObject },
      closuresStore,
      projectDateStore,
      selectedStartDate,
      selectedEndDate,
    } = this.props
    const locationClosures =
      closuresStore.closuresByLocationIdMap[dataObject.id]

    return locationClosures?.filter(closure =>
      closure.areWithinSelectedRange(
        projectDateStore,
        selectedStartDate,
        selectedEndDate || selectedStartDate,
      ),
    )
  }

  private get closuresList(): React.ReactNode {
    return (
      <>
        <div className="tw:text-label-lg-prominent">
          {Localization.translator.activeClosuresHeading}
        </div>
        {this.activeClosures.map((closure, index) => (
          <div
            className="tw:flex tw:min-w-0 tw:items-center tw:gap-1.5 tw:text-body-sm"
            key={index}
          >
            {this.getActiveClosureLabel(closure.closureInterval)}
          </div>
        ))}
      </>
    )
  }

  /**
   * Closures only, deliberately — this pill does NOT consult operating hours, so
   * a location outside today's window, or shut on today's weekday, still reads
   * "Opened" beside the hours rendered above.
   *
   * Left as it is on purpose (STX1-893): the hours the card DISPLAYS were fixed
   * here, the state it REPORTS was not. Making it agree means the adapter's
   * two-part test — not closed by a closure, and
   * isIntervalWithinOperatingWindows over buildEffectiveOperatingIntervalsFmt —
   * but first someone has to decide what the selected range means for hours. The
   * branch below reports "open now" while now falls inside it and "not closed for
   * the entire period" otherwise, and the jobsite homepage has no equivalent to
   * copy. That question is why this is a separate ticket rather than a few lines.
   */
  private get isOpen(): boolean {
    const {
      item: { dataObject },
      closuresStore,
      selectedStartDate,
      selectedEndDate,
      projectDateStore,
    } = this.props

    return isLocationOpen(dataObject, {
      projectDateStore,
      closuresStore,
      selectedStartDate,
      selectedEndDate,
    })
  }

  private getActiveClosureLabel = (
    closureInterval: ILocationClosureInterval,
  ): React.ReactNode => {
    const { isDaily, startDate, endDate } = closureInterval
    const {
      getTimeIntervalPerDayToDisplay,
      getMonthDayAndTimeIntervalPerDayToDisplay,
    } = this.props.projectDateStore
    const dateToDisplay = isDaily
      ? `${Localization.translator.daily}: ${getTimeIntervalPerDayToDisplay(
          startDate,
          endDate,
        )}`
      : getMonthDayAndTimeIntervalPerDayToDisplay(startDate, endDate)

    return (
      <>
        <CalendarX2 className="tw:size-4 tw:flex-none tw:text-destructive" />
        <span className="tw:truncate">{dateToDisplay}</span>
      </>
    )
  }
}
